/**
 * Schema Validator
 * 
 * Ensures all items match the canonical schema before insertion.
 * This is the single source of truth for item structure.
 * 
 * NO ITEM may enter deepsearch.items without passing validation.
 */

/**
 * CANONICAL SCHEMA
 * 
 * This is the required structure for ALL items in the vault.
 */
export const CANONICAL_SCHEMA = {
  // Identity
  id: 'uuid',
  userId: 'uuid',
  
  // Timestamps
  createdAt: 'timestamp',
  updatedAt: 'timestamp',
  
  // Content
  title: 'string',
  description: 'string|null',
  textContent: 'string|null',
  
  // Media
  url: 'string|null',
  imageUrl: 'string|null',
  thumbnailUrl: 'string|null',
  images: 'array',
  
  // Classification
  sourceType: 'string',
  channel: 'string',
  
  // Tags
  tags: 'array',
  autoTags: 'object',
  
  // Enrichment (REQUIRED)
  extractedThemes: 'array',
  microTopics: 'array',
  entities: 'array',
  
  // AI Analysis (REQUIRED)
  aiAnalysis: 'object',
  
  // Embeddings (REQUIRED)
  embedding: 'array|null',
  embeddingModel: 'string|null',
  
  // Metadata
  meta: 'object',
  
  // Traceability
  originalSourceTable: 'string',
  originalSourceId: 'string',
};

/**
 * REQUIRED FIELDS
 * 
 * These fields MUST exist and have valid values.
 */
const REQUIRED_FIELDS = [
  'id',
  'userId',
  'createdAt',
  'updatedAt',
  'title',
  'sourceType',
  'channel',
  'extractedThemes',
  'microTopics',
  'aiAnalysis',
  'originalSourceTable',
  'originalSourceId',
];

/**
 * REQUIRED ENRICHMENT FIELDS
 * 
 * These fields must be populated by the enrichment pipeline.
 */
const REQUIRED_ENRICHMENT = [
  'extractedThemes',
  'microTopics',
  'entities',
  'aiAnalysis',
];

/**
 * Validate item against canonical schema
 */
export function validateItem(item, options = {}) {
  const { 
    allowMissingEnrichment = false,
    strict = true,
  } = options;
  
  const errors = [];
  const warnings = [];
  
  // 1. Check required fields
  REQUIRED_FIELDS.forEach(field => {
    if (item[field] === undefined || item[field] === null) {
      errors.push(`Missing required field: ${field}`);
    }
  });
  
  // 2. Check field types
  if (item.id && typeof item.id !== 'string') {
    errors.push('id must be a string (UUID)');
  }
  
  if (item.userId && typeof item.userId !== 'string') {
    errors.push('userId must be a string (UUID)');
  }
  
  if (item.title && typeof item.title !== 'string') {
    errors.push('title must be a string');
  }
  
  if (item.extractedThemes && !Array.isArray(item.extractedThemes)) {
    errors.push('extractedThemes must be an array');
  }
  
  if (item.microTopics && !Array.isArray(item.microTopics)) {
    errors.push('microTopics must be an array');
  }
  
  if (item.entities && !Array.isArray(item.entities)) {
    errors.push('entities must be an array');
  }
  
  if (item.aiAnalysis && typeof item.aiAnalysis !== 'object') {
    errors.push('aiAnalysis must be an object');
  }
  
  // 3. Check enrichment completeness
  if (!allowMissingEnrichment) {
    if (!item.extractedThemes || item.extractedThemes.length === 0) {
      errors.push('extractedThemes cannot be empty');
    }
    
    if (!item.microTopics || item.microTopics.length === 0) {
      errors.push('microTopics cannot be empty');
    }
    
    if (!item.aiAnalysis || !item.aiAnalysis.summary) {
      errors.push('aiAnalysis.summary is required');
    }
    
    if (!item.aiAnalysis || !item.aiAnalysis.whyItMatters) {
      warnings.push('aiAnalysis.whyItMatters is missing');
    }
    
    if (!item.aiAnalysis || !item.aiAnalysis.relatedConcepts) {
      warnings.push('aiAnalysis.relatedConcepts is missing');
    }
  }
  
  // 4. Check traceability
  if (!item.originalSourceTable) {
    errors.push('originalSourceTable is required for traceability');
  }
  
  if (!item.originalSourceId) {
    errors.push('originalSourceId is required for traceability');
  }
  
  // 5. Check channel
  if (!item.channel || item.channel.trim().length === 0) {
    warnings.push('channel should be set for proper filtering');
  }
  
  // 6. Strict mode checks
  if (strict) {
    // Check for pending:// URLs (forbidden)
    if (item.url && item.url.startsWith('pending://')) {
      errors.push('pending:// URLs are forbidden - upload file first');
    }
    
    // Check embedding (can be null - will be generated later)
    if (item.embedding !== null && item.embedding !== undefined && !Array.isArray(item.embedding)) {
      errors.push('embedding must be an array or null');
    }
    
    if (item.embedding && item.embedding.length !== 1536) {
      warnings.push(`embedding should be 1536 dimensions, got ${item.embedding?.length || 0}`);
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate enrichment quality
 * 
 * Checks that enrichment fields meet quality standards.
 */
export function validateEnrichmentQuality(item) {
  const errors = [];
  const warnings = [];
  
  // 1. Check themes are from controlled vocabulary
  const ALLOWED_THEMES = [
    'fashion', 'style', 'fitness', 'exercise', 'health', 'nutrition',
    'sales', 'business', 'marketing', 'productivity', 'relationships',
    'travel', 'restaurants', 'food', 'tech', 'design', 'creativity',
    'self improvement', 'home', 'money', 'learning', 'psychology',
    'lifestyle', 'career',
  ];
  
  if (item.extractedThemes) {
    const invalidThemes = item.extractedThemes.filter(
      theme => !ALLOWED_THEMES.includes(theme.toLowerCase())
    );
    
    if (invalidThemes.length > 0) {
      errors.push(`Invalid themes: ${invalidThemes.join(', ')}`);
    }
    
    if (item.extractedThemes.length > 3) {
      warnings.push('More than 3 themes - should be 1-3 max');
    }
  }
  
  // 2. Check microtopics are specific
  if (item.microTopics) {
    const genericTopics = item.microTopics.filter(topic => {
      const lower = topic.toLowerCase();
      return lower.includes('general') || 
             lower.includes('various') || 
             lower.includes('tips') ||
             lower === 'business' ||
             lower === 'fitness' ||
             lower === 'fashion';
    });
    
    if (genericTopics.length > 0) {
      warnings.push(`Generic microtopics detected: ${genericTopics.join(', ')}`);
    }
    
    if (item.microTopics.length > 10) {
      warnings.push('More than 10 microtopics - should be 3-10 max');
    }
  }
  
  // 3. Check AI analysis completeness
  if (item.aiAnalysis) {
    if (!item.aiAnalysis.summary || item.aiAnalysis.summary.length < 20) {
      errors.push('aiAnalysis.summary too short or missing');
    }
    
    if (!item.aiAnalysis.whyItMatters || item.aiAnalysis.whyItMatters.length < 20) {
      warnings.push('aiAnalysis.whyItMatters too short or missing');
    }
    
    if (!item.aiAnalysis.relatedConcepts || item.aiAnalysis.relatedConcepts.length === 0) {
      warnings.push('aiAnalysis.relatedConcepts is empty');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Assert item is valid (throws if not)
 * 
 * Use this before database writes to ensure data integrity.
 */
export function assertValid(item, options = {}) {
  const validation = validateItem(item, options);
  
  if (!validation.valid) {
    const error = new Error(`Schema validation failed: ${validation.errors.join(', ')}`);
    error.validationErrors = validation.errors;
    error.validationWarnings = validation.warnings;
    throw error;
  }
  
  // Log warnings (don't throw)
  if (validation.warnings.length > 0) {
    console.warn('[SchemaValidator] Warnings:', validation.warnings);
  }
  
  return true;
}

/**
 * Get validation report for debugging
 */
export function getValidationReport(item) {
  const schemaValidation = validateItem(item, { allowMissingEnrichment: false, strict: true });
  const qualityValidation = validateEnrichmentQuality(item);
  
  return {
    schema: schemaValidation,
    quality: qualityValidation,
    overall: {
      valid: schemaValidation.valid && qualityValidation.valid,
      errors: [...schemaValidation.errors, ...qualityValidation.errors],
      warnings: [...schemaValidation.warnings, ...qualityValidation.warnings],
    },
  };
}

/**
 * Expose for debugging
 */
if (typeof window !== 'undefined') {
  window.schemaValidator = {
    validateItem,
    validateEnrichmentQuality,
    getValidationReport,
    CANONICAL_SCHEMA,
  };
  
  console.log('[SchemaValidator] Validation tools available: window.schemaValidator');
}

export default {
  validateItem,
  validateEnrichmentQuality,
  assertValid,
  getValidationReport,
  CANONICAL_SCHEMA,
  REQUIRED_FIELDS,
  REQUIRED_ENRICHMENT,
};

